// libraries
import {groupBy, map, size, pick, uniqueId, isNil} from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';

// shared
import Icon from './icon';

import 'stylesheets/components/shared-dropdown';

class Dropdown extends React.Component {
  static propTypes = {
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onSelect: PropTypes.func,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.oneOfType([
        PropTypes.string, PropTypes.number
      ]).isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string, PropTypes.number
      ]).isRequired,
      text: PropTypes.string.isRequired,
      'aria-label': PropTypes.string,
      group: PropTypes.string
    })),
    value: PropTypes.oneOfType([
      PropTypes.string, PropTypes.number
    ]),
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string
    ]),
    required: PropTypes.bool,
    'aria-labelledby': PropTypes.string,
    focusOnMount: PropTypes.bool,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
    fitToContent: PropTypes.bool,
    disabled: PropTypes.bool,
    autoCompleteType: PropTypes.string,
    autoComplete: PropTypes.bool,
    size: PropTypes.oneOf([
      'small', 'medium', 'large'
    ]),
    name: PropTypes.string,
    placeholder: PropTypes.string,
    // if true, the dropdown display will show the 'value' for the selected option, not the 'text'
    useValueForDisplay: PropTypes.bool
  };

  static defaultProps = {
    options: [],
    value: '',
    fitToContent: false,
    placeholder: 'Select',
    autoCompleteType: null,
    autoComplete: true,
    size: 'medium'
  };

  UNSAFE_componentWillMount() {
    this.uniqueId = uniqueId('dropdown');
  }

  componentDidMount() {
    if (this.props.focusOnMount) {
      this.dropdown.focus();
    }
  }

  renderOptions = () => {
    const {options, value, useValueForDisplay} = this.props;
    const groups = groupBy(options, 'group');

    const optionify = (opts) => {
      return map(opts, (option) => {
        let text;
        if (useValueForDisplay && option.value === value) {
          text = value;
        } else {
          text = option.text;
        }
        return (
          <option key={option._id} value={option.value} aria-label={options['aria-label']}>
            {text}
          </option>
        );
      });
    };

    if (size(groups) === 1) {
      return optionify(options);
    }

    return map(groups, (opts, group) => {
      return (
        <optgroup key={group} label={group}>
          {optionify(opts)}
        </optgroup>
      );
    });
  };

  handleChange = (e) => {
    const {onSelect, onChange} = this.props;
    if (!isNil(onSelect)) {onSelect({value: e.target.value});}
    if (!isNil(onChange)) {onChange(e);}
  }

  render() {
    const {
      value,
      onFocus,
      onBlur,
      error,
      required,
      className,
      containerClassName,
      fitToContent,
      disabled,
      size,
      placeholder,
      autoCompleteType,
      autoComplete,
      name
    } = this.props;
    const ariaProps = pick(this.props, ['aria-labelledby']);
    if (error) {
      ariaProps['aria-describedby'] = `${this.uniqueId}-error`;
      ariaProps['aria-invalid'] = true;
    }

    return (
      <div className={classnames('shared-dropdown', {'fit-content': fitToContent}, containerClassName) }>
        <select
          id={this.uniqueId}
          required={required}
          value={value}
          onFocus={onFocus}
          onBlur={onBlur}
          name={name}
          onChange={this.handleChange}
          className={classnames('shared-dropdown-select', className, {placeholder: !value}, {error}, {disabled}, size)}
          ref={(dropdown) => { this.dropdown = dropdown; }}
          disabled={disabled}
          autoComplete={autoComplete ? autoCompleteType : 'off'}
          {...ariaProps}
        >
          <option hidden value="">{placeholder}</option>
          {this.renderOptions()}
        </select>
        <Icon iconType="chevron-down" className={classnames('shared-dropdown-arrow', {disabled}, size)} />
        {error && <p id={`${this.uniqueId}-error`} className="shared-error-text" role="alert">Error: {error}</p>}
      </div>
    );
  }
}

export default Dropdown;
